import { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { BookOpen, Star, Zap, Ghost } from "lucide-react";
import { PenMark } from "./BrandLogo";
import { RevealLine } from "./Reveal";
import { BOOKS, scrollToId } from "../data/books";

const OCTOBER = new Date().getMonth() === 9;

const useHalloweenCountdown = () => {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date(now.getFullYear(), 9, 31, 23, 59, 59);
      const diff = Math.max(0, end - now);
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, []);
  return t;
};

const HeroVideo = () => {
  const t = useHalloweenCountdown();
  const pad = (n) => String(n).padStart(2, "0");
  return (
  <motion.div
    data-testid="hero-video"
    initial={{ opacity: 0, y: 60, rotate: 0 }}
    animate={{ opacity: 1, y: 0, rotate: -1 }}
    transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
    whileHover={{ rotate: 0 }}
    className="relative mx-auto w-full max-w-md"
  >
    <div
      data-testid="halloween-countdown"
      className="mb-4 flex rotate-1 items-center justify-center gap-2 rounded-full border-2 border-ink bg-ink px-4 py-2.5 shadow-pop"
    >
      <Ghost className="h-4 w-4 text-banana" />
      <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-cream sm:text-sm">
        Halloween in <span className="text-banana">{t.d}d {pad(t.h)}:{pad(t.m)}:{pad(t.s)}</span>
      </span>
    </div>
    <div className="overflow-hidden rounded-2xl border-2 border-ink bg-ink shadow-pop-lg">
      <video
        data-testid="hero-video-player"
        controls
        preload="none"
        playsInline
        poster="/videos/cousins-together-poster.jpg"
        className="aspect-video w-full object-cover"
      >
        <source src="/videos/cousins-together.mp4" type="video/mp4" />
      </video>
    </div>
    <motion.div
      animate={{ y: [0, -12, 0], rotate: [-8, -4, -8] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      className="absolute -top-5 -left-4"
    >
      <PenMark className="w-14 h-14 drop-shadow-[3px_3px_0_#1A1B23]" />
    </motion.div>
    <motion.div
      animate={{ y: [0, 8, 0], rotate: [2, 4, 2] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
      className="mt-5 flex justify-center"
    >
      <span className="sticker rounded-full bg-crimson text-cream">
        <Star className="h-3.5 w-3.5 fill-current" /> Now showing: Halloween special
      </span>
    </motion.div>
    <p className="mt-5 text-center font-mono text-xs font-bold uppercase tracking-[0.25em] text-ink/50">
      Lil Momma &amp; Man-Man: A Spooky Halloween Adventure
    </p>
  </motion.div>
  );
};

const BookFan = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 60, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 60, damping: 16 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const fan = [
    { cover: (OCTOBER ? BOOKS[0] : BOOKS[2]).cover, alt: (OCTOBER ? BOOKS[0] : BOOKS[2]).title, cls: "-rotate-12 -translate-x-[58%] translate-y-8 scale-90 z-10", d: 0.55 },
    { cover: BOOKS[1].cover, alt: BOOKS[1].title, cls: "rotate-12 translate-x-[58%] translate-y-8 scale-90 z-10", d: 0.7 },
    { cover: (OCTOBER ? BOOKS[3] : BOOKS[0]).cover, alt: (OCTOBER ? BOOKS[3] : BOOKS[0]).title, cls: "z-20 -translate-y-2 scale-[1.04]", d: 0.85 },
  ];

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative mx-auto flex h-[420px] w-full max-w-md items-center justify-center sm:h-[500px]"
      style={{ perspective: 1200 }}
      data-testid="hero-book-fan"
    >
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative flex items-center justify-center">
        {fan.map((b) => (
          <div key={b.alt} className={`absolute ${b.cls}`}>
            <motion.img
              src={b.cover}
              alt={`${b.alt} book cover`}
              initial={{ opacity: 0, y: 90 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: b.d, ease: [0.16, 1, 0.3, 1] }}
              className="w-44 rounded-xl border-2 border-ink object-cover shadow-pop-lg sm:w-56"
              style={{ aspectRatio: "3/4" }}
            />
          </div>
        ))}
        <div className="w-44 sm:w-56" style={{ aspectRatio: "3/4" }} />
      </motion.div>

      <motion.div
        animate={{ y: [0, -12, 0], rotate: [-8, -4, -8] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-3 left-2 sm:left-6"
      >
        <PenMark className="w-14 h-14 drop-shadow-[3px_3px_0_#1A1B23]" />
      </motion.div>
      <motion.div
        animate={{ y: [0, 10, 0], rotate: [6, 10, 6] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
        className="sticker absolute -bottom-2 right-0 rotate-6 rounded-full bg-crimson text-cream sm:right-4"
      >
        <Star className="h-3.5 w-3.5 fill-current" /> {OCTOBER ? "Spooky season pick" : "5 books out now"}
      </motion.div>
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1.4 }}
        className="sticker absolute top-10 -right-1 -rotate-6 rounded-lg bg-banana sm:right-2"
      >
        <Zap className="h-3.5 w-3.5 fill-current" /> You got this!
      </motion.div>
    </div>
  );
};

export default function Hero() {
  return (
    <section id="home" data-testid="hero-section" className="halftone relative overflow-hidden pt-28 sm:pt-32">
      <div className="field-lines absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-16 sm:px-6 lg:grid-cols-12 lg:pb-24">
        <div className="lg:col-span-7">
          <RevealLine delay={0.1}>
            <span className="sticker -rotate-2 rounded-full bg-white">
              <PenMark className="h-4 w-4" /> Official children's book series
            </span>
          </RevealLine>

          <h1 data-testid="hero-title" className="mt-6 font-head text-4xl font-black leading-[0.98] tracking-tight sm:text-5xl lg:text-6xl">
            <RevealLine delay={0.25}>Empowering kids to</RevealLine>
            <RevealLine delay={0.38}>
              <span className="text-maroon">stand tall,</span> <span className="text-royal">shine bright</span>
            </RevealLine>
            <RevealLine delay={0.51}>
              &amp; <span className="relative inline-block">believe
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 220 12" fill="none" aria-hidden="true">
                  <path d="M3 9 C 60 2, 160 2, 217 8" stroke="#E5A922" strokeWidth="6" strokeLinecap="round" />
                </svg>
              </span> in themselves.
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg"
          >
            Join cousins <span className="font-bold text-crimson">Lil Momma</span> and{" "}
            <span className="font-bold text-royal">Man-Man</span> on funny, real-life adventures about kindness,
            healthy choices, and believing in yourself — because you hold the pen to your own story.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-books"
              onClick={() => scrollToId("books")}
              className="group flex items-center gap-2.5 rounded-full border-2 border-ink bg-maroon px-7 py-3.5 font-head text-base font-extrabold uppercase tracking-wide text-cream shadow-pop transition-all duration-200 hover:-translate-y-1 hover:shadow-pop-lg active:translate-y-0 active:shadow-pop-sm"
            >
              <BookOpen className="h-5 w-5 transition-transform duration-300 group-hover:rotate-[-12deg]" />
              Explore the 5 books
            </button>
            <button
              data-testid="hero-cta-author"
              onClick={() => scrollToId("author")}
              className="rounded-full border-2 border-ink bg-white px-7 py-3.5 font-head text-base font-extrabold uppercase tracking-wide shadow-pop transition-all duration-200 hover:-translate-y-1 hover:bg-banana hover:shadow-pop-lg active:translate-y-0 active:shadow-pop-sm"
            >
              Meet Sugarfoot
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          {OCTOBER ? <HeroVideo /> : <BookFan />}
        </div>
      </div>
    </section>
  );
}
